import type{ PhotoInfo} from '@/types/photoApiDef'
import type{ KokubanInfo} from '@/types/kokubanApiDef'
import type{ ConstructionAccountInfo} from '@/types/constructionApiDef'
import type{ ZumenEntity, ZumenInfo, ZumenPage, FolderNameParts} from '@/types/zumenApiDef'
import type{ AppError} from '@/types/appErrorDef'
import { defineStore } from "pinia"
import { ref, computed } from 'vue'
import { idb }  from '@/idb/idb'
import { toFullWidth } from '@/utils/strings'
import { isDisplayOnPhotoList, isDisplayOnDeletedList } from '@/utils/photo_conditions'
import { isDisplayOnKokubanList, isDisplayOnKokubanDeletedList } from '@/utils/kokuban_conditions'
import { useAwsStore } from "@/stores/aws"
import { useAppErrorStore } from "@/stores/appError"
import { useWorkerStore } from "@/stores/worker"
import { useRetrieveTasksStore } from "@/stores/retrieveTasks"
import { useSessionIdStore } from "@/stores/sessionId"
import { useLoginStore } from '@/stores/login'
import { useLoadingStore } from "@/stores/loading"

export const useContentsStore = defineStore('contents', () => {
  const { getSessionId } = useSessionIdStore()

  // 最終更新時刻テーブルから最終更新時刻を取得する
  const getLatestModified = async(cloudConstructionId: string, itemType: string) => {
    return await (await idb.getDb()).kuraemon_cloud2_latest_modified_at
    .get({
      'cloud_construction_id':cloudConstructionId,
      'item_type': itemType,
    })
  }

  // 検索結果を取得する
  const getSearchResult = async(
    cloudConstructionId: string, 
    itemType: string, 
    searchWord: string
  ) => {
    let result = null
    // 最終更新時刻テーブルから最終更新時刻を取得する
    const latestModified = await getLatestModified(cloudConstructionId, itemType)

    // 最終更新時刻が設定されている場合
    if(latestModified){
      // 検索結果テーブルより検索結果を取得する
      const searchResult = await (await idb.getDb()).kuraemon_cloud2_search_result
      .get({
        'cloud_construction_id':cloudConstructionId,
        'item_type': itemType,
        'latest_modified_at': latestModified.latest_modified_at,
        'search_word': searchWord,
      })
      // 検索結果があり場合
      if(searchResult){
        result = searchResult
      }
    }
    return result
  }
  // 検索結果を検索結果テーブルに保存する
  const saveSearchResult = async(
    cloudConstructionId: string, 
    itemType: string, 
    searchWord: string, 
    searchResult: Array<string>
  ) =>{
    // 最終更新時刻テーブルから最終更新時刻を取得する
    const latestModified = await getLatestModified(cloudConstructionId, itemType)

    // 最終更新時刻が設定されている場合
    if(latestModified){
      // 検索結果を検索結果テーブルに保存する
      await (await idb.getDb()).kuraemon_cloud2_search_result
      .put({
      'cloud_construction_id':cloudConstructionId,
      'item_type': itemType,
      'latest_modified_at': latestModified.latest_modified_at,
      'search_word': searchWord,
      'search_result': searchResult,
      })
    }
  }

  /**
   * 起動対象とするアイテムタイプを返す（既に起動中は省く）
   * @param {*} context 
   * @param {string} cloudConstructionId 工事ID
   * @param {string[]} itemTypes 対象のアイテムタイプ
   * @returns 起動対象とするアイテムタイプ。ない場合はundefined
   */
  const getStartItemTypes = async (cloudConstructionId: string, itemTypes: Array<string>) => {
    // 既存タスクの存在チェック
    const { getRunningItemTypes } = useRetrieveTasksStore()
    const runningItemTypes = getRunningItemTypes({
      cloud_construction_id: cloudConstructionId,
      item_types: itemTypes,
    });
    const startItemTypes = (() => {
      if(!runningItemTypes){
        // 既存のtaskがない場合, 全種類を起動
        return itemTypes;
      }
    
      // 起動していないtaskのみを起動対象にする(既に取得中のタスクは起動しない)
      return itemTypes.filter(it => !runningItemTypes.includes(it))
    })()

    if(0 < startItemTypes.length){
      const { startTasks } = useRetrieveTasksStore()
      await startTasks({
        cloud_construction_id: cloudConstructionId,
        item_types: startItemTypes
      });
      return startItemTypes
    }
  }
  type ZumenRec = ZumenInfo & { cloud_construction_id: string, zumen_id: string }
  
  type State = {
    cloudConstructionId: string | null,
    accountInfo: ConstructionAccountInfo | null,
    photosLoading: boolean,
    constructionPhotos: Array<PhotoInfo> | null,
    searchResultPhotoKeys: Array<string> | null,
    deletedConstructionPhotos: Array<PhotoInfo> | null,
    kokubansLoading: boolean,
    constructionKokubans: Array<KokubanInfo> | null,
    searchResultKokubanHashes: Array<string> | null,
    deletedConstructionKokubans: Array<KokubanInfo> | null,
    zumensLoading: boolean
    constructionZumens: Array<ZumenRec> | null
    zumenEmptyFolderNameList: FolderNameParts | null
    deletedConstructionZumens: Array<ZumenRec> | null
    zumenEntitiesLoading: boolean
    constructionZumenEntities: Array<ZumenEntity> | null
    zumenPagesLoading: boolean
    constructionZumenPages: Array<ZumenPage> | null
  }
  const state = ref<State>({
    cloudConstructionId: null,          // クラウド工事ID
    accountInfo: null,                  // 工事でのログインアカウント情報
    photosLoading: false,               // 写真をロード中
    constructionPhotos: null,           // クラウド工事IDの写真一覧
    searchResultPhotoKeys: null,        // 検索結果photoKey
    deletedConstructionPhotos: null,    // クラウド工事IDの削除された写真一覧
    kokubansLoading: false,             // 黒板をロード中
    constructionKokubans: null,         // クラウド工事IDの黒板一覧
    searchResultKokubanHashes: null,    // 検索結果kokubanHash
    deletedConstructionKokubans: null,  // クラウド工事IDの削除された黒板一覧
    zumensLoading: false,               // 図面をロード中
    constructionZumens: null,           // クラウド工事IDの図面一覧
    zumenEmptyFolderNameList: null,     // 図面フォルダ内の空のフォルダ名のリスト
    deletedConstructionZumens: null,    // クラウド工事IDの削除された図面一覧
    zumenEntitiesLoading: false,        // 図面エンティティをロード中
    constructionZumenEntities: null,    // クラウド工事IDの図面エンティティ一覧
    zumenPagesLoading: false,           // 図面ページをロード中
    constructionZumenPages: null,       // クラウド工事IDの図面ページ一覧
  })

  // クラウド工事ID
  const cloudConstructionId = computed(() => state.value.cloudConstructionId)

  // 工事でのログインアカウント情報
  const accountInfo = computed(() => state.value.accountInfo)

  // 写真をロード中
  const photosLoading = computed(() => state.value.photosLoading)
  // 写真一覧
  const constructionPhotos = computed(() => state.value.constructionPhotos)
  // 検索結果photoKey
  const searchResultPhotoKeys = computed(() => state.value.searchResultPhotoKeys)
  // 削除された写真一覧
  const deletedConstructionPhotos = computed(() => state.value.deletedConstructionPhotos)
  // 写真・撮影者一覧
  const photoShootingAccounts = computed(() => {
    if(!state.value.constructionPhotos) return []
    const accounts: {[shooting_account_id: string]: null} = {};
    state.value.constructionPhotos
      .filter(photo => photo.shooting_account_id)
      .map( photo => {
        accounts[photo.shooting_account_id] = null  
    })
    const accountList = []
    for (const shooting_account_id of Object.keys(accounts)) {
      accountList.push({shooting_account_id: shooting_account_id})
    }
    return accountList;
  })

  // 黒板をロード中
  const kokubansLoading = computed(() => state.value.kokubansLoading)
  // 黒板一覧
  const constructionKokubans = computed(() => state.value.constructionKokubans)
  // 検索結果kokubanHash
  const searchResultKokubanHashes = computed(() => state.value.searchResultKokubanHashes)
  // 黒板を使用している写真一覧
  const kokubanPhotos = computed(() => (kokubanHash: string) => {
    if (state.value.constructionPhotos){
      return state.value.constructionPhotos.filter(photo => photo.kokuban_hash === kokubanHash)
    }
    return null;
  });
  // 削除された黒板一覧
  const deletedConstructionKokubans = computed(() => state.value.deletedConstructionKokubans)

  // 図面をロード中
  const zumensLoading = computed(() => state.value.zumensLoading)
  // 図面一覧
  const constructionZumens = computed(() => state.value.constructionZumens)
  // 図面フォルダ内の空のフォルダ名のリスト
  const zumenEmptyFolderNameList = computed(() => state.value.zumenEmptyFolderNameList)
  // 削除された写真一覧
  const deletedConstructionZumens = computed(() => state.value.deletedConstructionZumens)
  // 図面エンティティをロード中
  const zumenEntitiesLoading = computed(() => state.value.zumenEntitiesLoading)
  // 図面ページをロード中
  const zumenPagesLoading = computed(() => state.value.zumenPagesLoading)
  // 図面ページ一覧
  const constructionZumenPages = computed(() => (zumenId: string) => {
    if(!state.value.constructionZumenPages) return null
    if(!state.value.constructionZumenEntities) return null

    // 図面IDで絞り込み
    const zumenPages = state.value.constructionZumenPages.filter(zumen => zumen.zumen_id === zumenId)

    // 図面エンティティ情報を追加する
    // キー：zumen_id、zumen_entity_code  
    let zumenPageEntities = zumenPages.map( zumenPage => {
      const zumenEntity = state.value.constructionZumenEntities 
        ? state.value.constructionZumenEntities
        .find( zumenEntity => zumenEntity.zumen_id === zumenPage.zumen_id && 
          zumenEntity.zumen_entity_code === zumenPage.zumen_entity_code)
        : null
      return{
        ...zumenPage,
        zumenEntity: zumenEntity ? zumenEntity : null,
      }
    })

    // タイミングによっては、この時点で図面エンティティ情報が取得できない場合がある
    // 図面エンティティ情報が取得できない場合（zumenEntityがnull）、ページ情報から削除する
    zumenPageEntities = zumenPageEntities.filter( zumenPage => zumenPage.zumenEntity)

    // ページ番号でソート
    return zumenPageEntities.sort((a, b) => (a.page_no - b.page_no))
  })

  const commit = {
    setCloudConstructionId(cloudConstructionId: string | null) {
      state.value.cloudConstructionId = cloudConstructionId
    },
    setAccountInfo(accountInfo: ConstructionAccountInfo) {
      state.value.accountInfo = accountInfo
    },

    setPhotosLoading(photosLoading: boolean) {
      state.value.photosLoading = photosLoading
    },
    setConstructionPhotos(constructionPhotos: Array<PhotoInfo> | null) {
      if(state.value.constructionPhotos) state.value.constructionPhotos.length = 0;
      state.value.constructionPhotos = constructionPhotos
    },
    setSearchResultPhotoKeys(searchResultPhotoKeys: Array<string> | null) {
      if(state.value.searchResultPhotoKeys) state.value.searchResultPhotoKeys.length = 0;
      state.value.searchResultPhotoKeys = searchResultPhotoKeys
    },
    setDeletedConstructionPhotos(deletedConstructionPhotos: Array<PhotoInfo> | null) {
      if(state.value.deletedConstructionPhotos) state.value.deletedConstructionPhotos.length = 0;
      state.value.deletedConstructionPhotos = deletedConstructionPhotos
    },
    setKokubansLoading(kokubansLoading: boolean) {
      state.value.kokubansLoading = kokubansLoading
    },
    setConstructionKokubans(constructionKokubans: Array<KokubanInfo> | null) {
      if(state.value.constructionKokubans) state.value.constructionKokubans.length = 0;
      state.value.constructionKokubans = constructionKokubans
    },
    setSearchResultKokubanHashes(searchResultKokubanHashes: Array<string> | null) {
      if(state.value.searchResultKokubanHashes) state.value.searchResultKokubanHashes.length = 0;
      state.value.searchResultKokubanHashes = searchResultKokubanHashes
    },
    setDeletedConstructionKokubans(deletedConstructionKokubans: Array<KokubanInfo> | null) {
      if(state.value.deletedConstructionKokubans) state.value.deletedConstructionKokubans.length = 0;
      state.value.deletedConstructionKokubans = deletedConstructionKokubans
    },
    setZumensLoading(zumensLoading: boolean) {
      state.value.zumensLoading = zumensLoading
    },
    setConstructionZumens(constructionZumens: Array<ZumenRec> | null) {
      if(state.value.constructionZumens) state.value.constructionZumens.length = 0;
      state.value.constructionZumens = constructionZumens
    },
    setDeletedConstructionZumens(deletedConstructionZumens: Array<ZumenRec> | null) {
      if(state.value.deletedConstructionZumens) state.value.deletedConstructionZumens.length = 0;
      state.value.deletedConstructionZumens = deletedConstructionZumens
    },
    setZumenEmptyFolderNameList(zumenEmptyFolderNameList: FolderNameParts | null) {
      state.value.zumenEmptyFolderNameList = zumenEmptyFolderNameList
    },

    setZumenEntitiesLoading(zumenEntitiesLoading: boolean) {
      state.value.zumenEntitiesLoading = zumenEntitiesLoading
    },
    setConstructionZumenEntities(constructionZumenEntities: Array<ZumenEntity> | null) {
      if(state.value.constructionZumenEntities) state.value.constructionZumenEntities.length = 0;
      state.value.constructionZumenEntities = constructionZumenEntities
    },
    setZumenPagesLoading(zumenPagesLoading: boolean) {
      state.value.zumenPagesLoading = zumenPagesLoading
    },
    setConstructionZumenPages(constructionZumenPages: Array<ZumenPage> | null) {
      if(state.value.constructionZumenPages) state.value.constructionZumenPages.length = 0;
      state.value.constructionZumenPages = constructionZumenPages
    },
  }
  const dispatch = {
    updateAccountInfo(accountInfo: ConstructionAccountInfo){ 
      commit.setAccountInfo(accountInfo)
    },

    // コンテンツ更新ワーカーのセット
    async setContentsStorageWorker(worker: Worker){
      const { initializeWorker } = useWorkerStore() 
      await initializeWorker(worker)
    },
    // コンテンツ保管場所のクリア
    async deleteContentsData(){ 
      await idb.deleteAllData()
    },
    // コンテンツ保管場所の初期化
    async initializeContentsStorage(){ 
      await idb.initializeDb()
    },
    // ワーカーからメッセージを受けた時の処理
    async onReceiveWorkerMessage(message: {
      cloud_construction_id: string, 
      name: string, 
      fetch_session_id: string,
      error: AppError,
    }){
      const cloudConstructionId = state.value.cloudConstructionId

      const allLoadedItemTypes: any = {
        allPhotoLoaded: 'photo',
        allKokubanLoaded: 'kokuban',
        allZumenLoaded: 'zumen', 
        allZumenEntityLoaded: 'zumenEntity',
        allZumenPageLoaded: 'zumenPage',
      }
      const allLoadedItemType = allLoadedItemTypes[message.name]
      if(allLoadedItemType){
        const { finishTask } = useRetrieveTasksStore()
        await finishTask({
          cloud_construction_id: message.cloud_construction_id,
          item_type: allLoadedItemType,
        })
      }

      if(message.name === 'photoLoaded' && message.cloud_construction_id === cloudConstructionId){
        await dispatch.checkFetchSessionId(message.fetch_session_id);
        await dispatch.updateConstructionPhotos();
        return;
      }
      if(message.name === 'allPhotoLoaded' && message.cloud_construction_id === cloudConstructionId){
        await dispatch.checkFetchSessionId(message.fetch_session_id);
        await dispatch.updateConstructionPhotos();
        await commit.setPhotosLoading(false)
        return;
      }
      if(message.name === 'kokubanLoaded' && message.cloud_construction_id === cloudConstructionId){
        await dispatch.checkFetchSessionId(message.fetch_session_id);
        await dispatch.updateConstructionKokubans();
        return;
      }
      if(message.name === 'allKokubanLoaded' && message.cloud_construction_id === cloudConstructionId){
        await dispatch.checkFetchSessionId(message.fetch_session_id);
        await dispatch.updateConstructionKokubans();
        await commit.setKokubansLoading(false)
        return;
      }
      if(message.name === 'zumenLoaded' && message.cloud_construction_id === cloudConstructionId){
        await dispatch.checkFetchSessionId(message.fetch_session_id);
        await dispatch.updateConstructionZumens();
        return;
      }
      if(message.name === 'allZumenLoaded' && message.cloud_construction_id === cloudConstructionId){
        await dispatch.checkFetchSessionId(message.fetch_session_id);
        await dispatch.updateConstructionZumens();
        await commit.setZumensLoading(false)
        return;
      }
      if(message.name === 'zumenEntityLoaded' && message.cloud_construction_id === cloudConstructionId){
        await dispatch.checkFetchSessionId(message.fetch_session_id);
        await dispatch.updateConstructionZumenEntities();
        return;
      }
      if(message.name === 'allZumenEntityLoaded' && message.cloud_construction_id === cloudConstructionId){
        await dispatch.checkFetchSessionId(message.fetch_session_id);
        await dispatch.updateConstructionZumenEntities();
        await commit.setZumenEntitiesLoading(false)
        return;
      }
      if(message.name === 'zumenPageLoaded' && message.cloud_construction_id === cloudConstructionId){
        await dispatch.checkFetchSessionId(message.fetch_session_id);
        await dispatch.updateConstructionZumenPages();
        return;
      }
      if(message.name === 'allZumenPageLoaded' && message.cloud_construction_id === cloudConstructionId){
        await dispatch.checkFetchSessionId(message.fetch_session_id);
        await dispatch.updateConstructionZumenPages();
        await commit.setZumenPagesLoading(false)
        return;
      }
      if(message.name === 'errorRaised') {
        // エラーの場合、ローディングを停止する
        const { __endLoading: endLoading } = useLoadingStore()
        endLoading()
        
        const appErrorStore = useAppErrorStore()
        await appErrorStore.updateAppError(message.error);
        return;
      }
    },
    async checkFetchSessionId(fetchSessionId: string){
      if(fetchSessionId !== getSessionId()){
        const { logoutAccount } = useLoginStore()
        logoutAccount({});
      }
    },
    // クラウド工事の写真データ取得をリクエスト
    async requestRetrievePhotos(cloudConstructionId: string){ 
      commit.setConstructionPhotos(null)
      commit.setDeletedConstructionPhotos(null)
      // ロード開始フラグ
      commit.setPhotosLoading(true)

      // 工事ID セット
      commit.setCloudConstructionId(cloudConstructionId);

      const msgs: any = {
        photo: {
          method: "getAllPhoto",
          cloud_construction_id: cloudConstructionId,
          accountInfo: state.value.accountInfo,
          fetch_session_id: getSessionId(),
        },
      }

      // 開始対象とするアイテムを取得
      const startItemTypes = await getStartItemTypes(
        cloudConstructionId,
        Object.keys(msgs)
      )

      if(startItemTypes){
        const messages = startItemTypes.map((it) => msgs[it])
        // バックグラウンドでデータを全件取得を開始
        const { backgroundTask } = useWorkerStore() 
        backgroundTask({ messages })
      }

      // 既にタスク開始中のアイテムについて、一回ロードする
      if(!startItemTypes || !startItemTypes.includes("photo")){
        await dispatch.updateConstructionPhotos()
      }
    },
    // バックグラウンドで取得したデータを取得する
    async updateConstructionPhotos(){ 
      await dispatch.initializeContentsStorage();
      const cloudConstructionId = state.value.cloudConstructionId;

      // 写真一覧
      const constructionPhotos = await (await idb.getDb()).kuraemon_cloud2_photo
        .where({
          'cloud_construction_id':cloudConstructionId,
        })
        .filter(ph => isDisplayOnPhotoList(ph))
        .toArray() as Array<PhotoInfo>

      // shooting_atの降順でソートする
      constructionPhotos.sort((a, b) => (b.shooting_at - a.shooting_at))

      commit.setConstructionPhotos(constructionPhotos)

      // 削除済みの写真一覧
      const deletedConstructionPhotos = await (await idb.getDb()).kuraemon_cloud2_photo
        .where({
          'cloud_construction_id':cloudConstructionId,
        })
        .filter(ph => isDisplayOnDeletedList(ph))
        .toArray() as Array<PhotoInfo>;

      // deleted_atの降順でソートする
      deletedConstructionPhotos.sort((a, b) => (b.deleted_at - a.deleted_at))

      commit.setDeletedConstructionPhotos(deletedConstructionPhotos)
    },
    // クラウド工事の写真データ再取得をリクエスト
    async requestReloadPhotos(cloudConstructionId: string){ 
      if(photosLoading.value) { // 写真ロード中
        return;
      }
      await dispatch.initializeContentsStorage();

      // 最終更新時刻をクリアする（これで再過去まで取得するようになる）
      await (await idb.getDb()).kuraemon_cloud2_latest_modified_at
        .delete([cloudConstructionId, 'photo']);

      dispatch.requestRetrievePhotos(cloudConstructionId)
    },
    // 写真検索結果(photo_key)のクリア
    async clearSearchResultPhotoKeys(){
      commit.setSearchResultPhotoKeys(null);
    },
    // 写真検索
    async searchPhotos(searchWord: string){
      await dispatch.initializeContentsStorage();
      const cloudConstructionId = state.value.cloudConstructionId as string;

      // 検索ワードを全角変換する
      const zenkakuSearchWord = toFullWidth(searchWord)

      // 検索結果テーブルより検索結果を取得する
      const result = await getSearchResult(cloudConstructionId, 'photo', zenkakuSearchWord)

      // 検索結果があり場合
      if(result){
        commit.setSearchResultPhotoKeys(result.search_result);
        return;
      }

      // 検索処理
      const zenkakuSearchWordList = zenkakuSearchWord.split("\u3000").filter(w => w);
      if (!zenkakuSearchWordList || zenkakuSearchWordList.length < 1) {
        // 検索ワードがない場合、写真検索結果をクリア
        commit.setSearchResultPhotoKeys(null);
        return
      }

      const whereObj = {
        'cloud_construction_id':cloudConstructionId,
      }
      const hitRecs = await (await idb.getDb()).kuraemon_cloud2_photo
        .where(whereObj)
        .filter( photo => isDisplayOnPhotoList(photo) && ( zenkakuSearchWordList.every( word => { return photo.search_words.some( (sw:any) => sw.includes(word) ) } ) ) )
        .toArray()

      const hitPhotoKeys = hitRecs.map( ph => ph.photo_key )

      // 検索結果を検索結果テーブルに保存する
      await saveSearchResult(cloudConstructionId, 'photo', zenkakuSearchWord, hitPhotoKeys)

      commit.setSearchResultPhotoKeys(hitPhotoKeys);
    },

    // クラウド工事の黒板データ取得をリクエスト
    async requestRetrieveKokubans(cloudConstructionId: string){ 
      // 写真データの初期化
      commit.setConstructionPhotos(null)
      commit.setDeletedConstructionPhotos(null)
      // 黒板データの初期化
      commit.setConstructionKokubans(null)
      commit.setDeletedConstructionKokubans(null)

      // ロード開始フラグ
      commit.setPhotosLoading(true)
      commit.setKokubansLoading(true)

      // 工事ID セット
      commit.setCloudConstructionId(cloudConstructionId);
      
      const msgs: any = {
        photo: {
          method: "getAllPhoto", 
          cloud_construction_id: cloudConstructionId,
          accountInfo: state.value.accountInfo,
          fetch_session_id: getSessionId(),
        },
        kokuban: {
          method: "getAllKokuban",
          cloud_construction_id: cloudConstructionId,
          accountInfo: state.value.accountInfo,
          fetch_session_id: getSessionId(),
        },
      }

      // 開始対象とするアイテムを取得
      const startItemTypes = await getStartItemTypes(
        cloudConstructionId,
        Object.keys(msgs)
      )

      if(startItemTypes){
        const messages = startItemTypes.map(it => msgs[it])
        // バックグラウンドでデータを全件取得を開始
        const { backgroundTask } = useWorkerStore() 
        backgroundTask({ messages })
      }

      // 既にタスク開始中のアイテムについて、一回ロードする
      if(!startItemTypes || !startItemTypes.includes("photo")){
        await dispatch.updateConstructionPhotos()
      }
      if(!startItemTypes || !startItemTypes.includes("kokuban")){
        await dispatch.updateConstructionKokubans()
      }
    },
    // クラウド工事の黒板データ取得をリクエスト(黒板のみ)
    async requestRetrieveKokubansOnly(cloudConstructionId: string){ 
      // 黒板データの初期化
      commit.setConstructionKokubans(null)
      commit.setDeletedConstructionKokubans(null)

      // ロード開始フラグ
      commit.setKokubansLoading(true)

      // 工事ID セット
      commit.setCloudConstructionId(cloudConstructionId);
      
      const msgs: any = {
        kokuban: {
          method: "getAllKokuban",
          cloud_construction_id: cloudConstructionId,
          accountInfo: state.value.accountInfo,
          fetch_session_id: getSessionId(),
        },
      }

      // 開始対象とするアイテムを取得
      const startItemTypes = await getStartItemTypes(
        cloudConstructionId,
        Object.keys(msgs)
      )

      if(startItemTypes){
        const messages = startItemTypes.map(it => msgs[it])
        // バックグラウンドでデータを全件取得を開始
        const { backgroundTask } = useWorkerStore() 
        backgroundTask({ messages })
      }

      // 既にタスク開始中のアイテムについて、一回ロードする
      if(!startItemTypes || !startItemTypes.includes("kokuban")){
        await dispatch.updateConstructionKokubans()
      }
    },
    // バックグラウンドで取得したデータを取得する
    async updateConstructionKokubans(){ 
      await dispatch.initializeContentsStorage();
      const cloudConstructionId = state.value.cloudConstructionId;

      const constructionKokubans = await (await idb.getDb()).kuraemon_cloud2_kokuban
        .where({
          'cloud_construction_id':cloudConstructionId,
        })
        .filter(kb => isDisplayOnKokubanList(kb))
        .toArray();

      commit.setConstructionKokubans(constructionKokubans.map(kokuban => {
        // 整理情報（黒板の裏）から絞込み項目を設定する
        kokuban.search_word_organize_info_constructions = 
          kokuban.organize_info_hierarchy.map((h:any) => (h.value || '(空欄)'));
        return kokuban
      }))

      // 削除済みの黒板一覧
      const deletedConstructionKokubans = await (await idb.getDb()).kuraemon_cloud2_kokuban
        .where({
          'cloud_construction_id':cloudConstructionId,
        })
        .filter(kb => isDisplayOnKokubanDeletedList(kb))
        .toArray();

      // deleted_atの降順でソートする
      deletedConstructionKokubans.sort((a, b) => (b.deleted_at - a.deleted_at))

      commit.setDeletedConstructionKokubans(deletedConstructionKokubans)
    },
    // クラウド工事の黒板と写真データ再取得をリクエスト
    async requestReloadKokubans(cloudConstructionId: string){ 
      if(kokubansLoading.value) { // 黒板ロード中
        return;
      }
      if(photosLoading.value) { // 写真ロード中
        return;
      }
      await dispatch.initializeContentsStorage();

      // 最終更新時刻をクリアする（これで再過去まで取得するようになる）
      await (await idb.getDb()).kuraemon_cloud2_latest_modified_at
        .delete([cloudConstructionId, 'kokuban']);
      await (await idb.getDb()).kuraemon_cloud2_latest_modified_at
        .delete([cloudConstructionId, 'photo']);

      dispatch.requestRetrieveKokubans(cloudConstructionId)
    },
    // 黒板検索結果(kokuban_hash)のクリア
    async clearSearchResultKokubanHashes(){
      commit.setSearchResultKokubanHashes(null);
    },
    // 黒板検索
    async searchKokubans(searchWord: string){
      await dispatch.initializeContentsStorage();
      const cloudConstructionId = state.value.cloudConstructionId as string;

      // 検索ワードを全角変換する
      const zenkakuSearchWord = toFullWidth(searchWord)

      // 検索結果テーブルより検索結果を取得する
      const result = await getSearchResult(cloudConstructionId, 'kokuban', zenkakuSearchWord)

      // 検索結果cacheがあり場合
      if(result){
        commit.setSearchResultKokubanHashes(result.search_result);
        return;
      }
      // 検索結果cacheなしの場合に、検索実施
      const zenkakuSearchWordList = zenkakuSearchWord.split("\u3000").filter(w => w);
      if (!zenkakuSearchWordList || zenkakuSearchWordList.length < 1) {
        // 検索ワードがない場合、写真検索結果をクリア
        commit.setSearchResultKokubanHashes(null);
        return
      }

      const whereObj = {
        'cloud_construction_id':cloudConstructionId,
      }
      const hitRecs = await (await idb.getDb()).kuraemon_cloud2_kokuban
        .where(whereObj)
        .filter( kokuban => isDisplayOnKokubanList(kokuban) && ( zenkakuSearchWordList.every( word => { return kokuban.search_words.some( (sw:any) => sw.includes(word) ) } ) ) )
        .toArray()

      const hitKokubanHashes = hitRecs.map( kk => kk.kokuban_hash )

      // 検索結果を検索結果テーブルに保存する
      await saveSearchResult(cloudConstructionId, 'kokuban', zenkakuSearchWord, hitKokubanHashes)

      commit.setSearchResultKokubanHashes(hitKokubanHashes);
    },
    // 指定工事のデータを削除する
    async deleteConstruction(cloudConstructionId: string){
      await dispatch.initializeContentsStorage();

      // 写真データの初期化
      commit.setConstructionPhotos(null)
      commit.setDeletedConstructionPhotos(null)

      // 黒板データの初期化
      commit.setConstructionKokubans(null)
      commit.setDeletedConstructionKokubans(null)

      // 図面データの初期化
      commit.setConstructionZumens(null)
      commit.setDeletedConstructionZumens(null)
      commit.setZumenEmptyFolderNameList(null)

      // 図面エンティティの初期化
      commit.setConstructionZumenEntities(null)

      // 図面ページの初期化
      commit.setConstructionZumenPages(null)

      // 工事ID
      commit.setCloudConstructionId(null);

      // 写真削除
      await (await idb.getDb()).kuraemon_cloud2_photo
        .where('cloud_construction_id').equals(cloudConstructionId).delete();

      // 黒板削除
      await (await idb.getDb()).kuraemon_cloud2_kokuban
        .where('cloud_construction_id').equals(cloudConstructionId).delete();
  
      // 図面削除
      await (await idb.getDb()).kuraemon_cloud2_zumen
        .where('cloud_construction_id').equals(cloudConstructionId).delete();

      // 図面フォルダ内の空のフォルダ名のリスト削除
      await (await idb.getDb()).kuraemon_cloud2_construction_empty_folder_name_parts_list
        .where('cloud_construction_id').equals(cloudConstructionId).delete();

      // 図面エンティティ削除
      await (await idb.getDb()).kuraemon_cloud2_zumen_entity
        .where('cloud_construction_id').equals(cloudConstructionId).delete();

      // 図面ページ削除
      await (await idb.getDb()).kuraemon_cloud2_zumen_page
        .where('cloud_construction_id').equals(cloudConstructionId).delete();
    },
    // バックグラウンドタスクの停止
    async stopWorkerJobs(){ 
      await dispatch.initializeContentsStorage();
      //現在のジョブを全て停止する
      await (await idb.getDb()).kuraemon_cloud2_job.clear();
      const { clearTasks } = useRetrieveTasksStore()
      await clearTasks()
    },

    // クラウド工事の図面データ取得をリクエスト
    async requestRetrieveZumens(cloudConstructionId: string){ 
      // 図面データの初期化
      commit.setConstructionZumens(null)
      commit.setDeletedConstructionZumens(null)
      commit.setZumenEmptyFolderNameList(null)
      commit.setConstructionZumenEntities(null)
      commit.setConstructionZumenPages(null)
      
      // ロード開始フラグ
      commit.setZumensLoading(true)
      commit.setZumenEntitiesLoading(true)
      commit.setZumenPagesLoading(true)

      // 工事ID セット後、既存の画像を表示する
      commit.setCloudConstructionId(cloudConstructionId);
      // dispatch.updateConstructionZumens');
      
      const msgs: any = {
        zumen: {
          method: "getAllZumen",
          cloud_construction_id: cloudConstructionId,
          accountInfo: state.value.accountInfo,
          fetch_session_id: getSessionId(),
        },
        zumenEntity: {
          method: "getAllZumenEntity",
          cloud_construction_id: cloudConstructionId,
          accountInfo: state.value.accountInfo,
          fetch_session_id: getSessionId(),
        },
        zumenPage: {
          method: "getAllZumenPage",
          cloud_construction_id: cloudConstructionId,
          accountInfo: state.value.accountInfo,
          fetch_session_id: getSessionId(),
        },
      }

      // 開始対象とするアイテムを取得
      const startItemTypes = await getStartItemTypes(
        cloudConstructionId,
        Object.keys(msgs)
      )

      if(startItemTypes){
        const messages = startItemTypes.map(it => msgs[it])
        // バックグラウンドで図面データを全件取得を開始
        const { backgroundTask } = useWorkerStore() 
        backgroundTask({ messages })
      }

      // 既にタスク開始中のアイテムについて、一回ロードする
      if(!startItemTypes || !startItemTypes.includes("zumen")){
        await dispatch.updateConstructionZumens()
      }
      if(!startItemTypes || !startItemTypes.includes("zumenEntity")){
        await dispatch.updateConstructionZumenEntities()
      }
      if(!startItemTypes || !startItemTypes.includes("zumenPage")){
        await dispatch.updateConstructionZumenPages()
      }
    },
    // バックグラウンドで取得したデータを取得する
    async updateConstructionZumens(){ 
      await dispatch.initializeContentsStorage();
      const cloudConstructionId = state.value.cloudConstructionId;

      // 図面一覧
      const constructionZumens = await (await idb.getDb()).kuraemon_cloud2_zumen
        .where({
          'cloud_construction_id':cloudConstructionId,
        })
        .filter( zumen => zumen.zumen_status === 'valid' || zumen.zumen_status === 'processing' || zumen.zumen_status === 'error')
        .toArray();

      // created_atの降順でソートする
      constructionZumens.sort((a, b) => (b.created_at - a.created_at))

      commit.setConstructionZumens(constructionZumens)

      // 削除済みの図面一覧
      const deletedConstructionZumens = await (await idb.getDb()).kuraemon_cloud2_zumen
        .where({
          'cloud_construction_id':cloudConstructionId,
          'zumen_status': 'deleted',
        })
        .toArray();

      // deleted_atの降順でソートする
      deletedConstructionZumens.sort((a, b) => (b.deleted_at - a.deleted_at))

      commit.setDeletedConstructionZumens(deletedConstructionZumens)

      // 図面フォルダ内の空のフォルダ名のリスト
      const zumenEmptyFolderNameList = await (await idb.getDb()).kuraemon_cloud2_construction_empty_folder_name_parts_list
        .get({'cloud_construction_id':cloudConstructionId})
      
      if (zumenEmptyFolderNameList){
        commit.setZumenEmptyFolderNameList(zumenEmptyFolderNameList.folder_name_list)
      }
    },
    // バックグラウンドで取得したデータを取得する
    async updateConstructionZumenEntities(){ 
      await dispatch.initializeContentsStorage();
      const cloudConstructionId = state.value.cloudConstructionId;

      // 図面エンティティ一覧
      const constructionEntities = await (await idb.getDb()).kuraemon_cloud2_zumen_entity
        .where({
          'cloud_construction_id':cloudConstructionId,
        })
        .toArray();

      commit.setConstructionZumenEntities(constructionEntities)
    },
    // バックグラウンドで取得したデータを取得する
    async updateConstructionZumenPages(){ 
      await dispatch.initializeContentsStorage();
      const cloudConstructionId = state.value.cloudConstructionId;

      // 図面ページ一覧
      const constructionPages = await (await idb.getDb()).kuraemon_cloud2_zumen_page
        .where({
          'cloud_construction_id':cloudConstructionId,
        })
        .toArray();

      commit.setConstructionZumenPages(constructionPages)
    },

    // 図面エンティティのサムネイルのURLを更新する
    async updateZumenUrl(zumenEntity: any){
      const awsStore = useAwsStore()

      await dispatch.initializeContentsStorage();
      const cloudConstructionId = state.value.cloudConstructionId as string

      // URLの生成 
      const args = {
        cloud_construction_id: cloudConstructionId,
        zumenEntity: zumenEntity,
      };
      const thumbnail = await awsStore.buildZumenUrl(args)
      return thumbnail
    },
  }

  return {
    cloudConstructionId,
    accountInfo,
    photosLoading,
    constructionPhotos,
    searchResultPhotoKeys,
    deletedConstructionPhotos,
    photoShootingAccounts,
    kokubansLoading,
    constructionKokubans,
    searchResultKokubanHashes,
    kokubanPhotos,
    deletedConstructionKokubans,
    zumensLoading,
    constructionZumens,
    zumenEmptyFolderNameList,
    deletedConstructionZumens,
    zumenEntitiesLoading,
    zumenPagesLoading,
    constructionZumenPages,

    dispatch
  }
})